import { useTranslation } from "react-i18next";
import i18n, { getLocale, translate as tr } from "../../i18n";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import './CheckInWidget.css';

// ── Formatters ────────────────────────────────────────────────────────────────

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0h 00m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

function formatElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTime(isoString) {
  if (!isoString) return '--:--';
  return new Date(isoString).toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
}

function getGreeting(h) {
  if (h < 12) return tr("Good morning");
  if (h < 17) return tr("Good afternoon");
  return tr("Good evening");
}

// ── Sky theme — changes per hour ──────────────────────────────────────────────

function getSkyTheme(h) {
  // [sky-top, sky-mid, sky-btm, horizon-glow, orb-color, orb-glow, isNight, showClouds]
  if (h < 5)  return { top:'#020818', mid:'#09122a', btm:'#152240', hglow:'rgba(20,45,110,.55)',  orb:'#dbd5b5', glow:'rgba(200,192,155,.20)', night:true,  clouds:false };
  if (h < 6)  return { top:'#070d20', mid:'#1c0e30', btm:'#461e14', hglow:'rgba(130,55,25,.60)',  orb:'#c8a472', glow:'rgba(188,146,88,.30)',  night:true,  clouds:false };
  if (h < 7)  return { top:'#131d50', mid:'#782c5e', btm:'#de5028', hglow:'rgba(225,105,45,.78)', orb:'#ffe055', glow:'rgba(255,188,55,.68)',  night:false, clouds:false };
  if (h < 9)  return { top:'#1e5e9e', mid:'#5a9ebc', btm:'#eeB640', hglow:'rgba(238,168,55,.48)', orb:'#ffe050', glow:'rgba(255,218,68,.52)',  night:false, clouds:true  };
  if (h < 12) return { top:'#1864a4', mid:'#4094be', btm:'#8ccce6', hglow:'rgba(130,205,232,.32)',orb:'#fff4be', glow:'rgba(255,238,168,.40)', night:false, clouds:true  };
  if (h < 15) return { top:'#0e4e98', mid:'#3284c0', btm:'#74c4e0', hglow:'rgba(95,192,228,.28)', orb:'#fffbd8', glow:'rgba(255,250,196,.34)', night:false, clouds:true  };
  if (h < 17) return { top:'#165ca4', mid:'#4290c8', btm:'#94cee8', hglow:'rgba(148,206,232,.30)',orb:'#ffe680', glow:'rgba(255,218,108,.40)', night:false, clouds:true  };
  if (h < 19) return { top:'#161860', mid:'#7c2c6e', btm:'#e44418', hglow:'rgba(228,88,25,.74)',  orb:'#ff7630', glow:'rgba(255,96,36,.68)',   night:false, clouds:false };
  if (h < 21) return { top:'#0c0820', mid:'#2e0c3c', btm:'#782012', hglow:'rgba(115,38,18,.58)',  orb:'#be7272', glow:'rgba(175,88,78,.32)',   night:true,  clouds:false };
  return             { top:'#020818', mid:'#09122a', btm:'#152240', hglow:'rgba(20,45,110,.55)',  orb:'#dbd5b5', glow:'rgba(200,192,155,.20)', night:true,  clouds:false };
}

// ── Orb arc position ──────────────────────────────────────────────────────────
// Sun:  6 am (left horizon) → 6 pm (right horizon), arcing overhead
// Moon: 6 pm (left horizon) → 6 am (right horizon)

function getOrbPosition(date) {
  const h = date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
  let t, isMoon;

  if (h >= 6 && h <= 18) {
    t      = (h - 6) / 12;
    isMoon = false;
  } else {
    t      = h > 18 ? (h - 18) / 12 : (h + 6) / 12;
    isMoon = true;
  }

  const angle      = t * Math.PI;
  const sinA       = Math.sin(angle);
  const nearHorizon = sinA < 0.18; // within ~10° of horizon

  return {
    // viewBox is 1000 × 240; orb travels 8%→92% across width
    px: (0.08 + t * 0.84) * 1000,
    // horizon sits at y≈175 (73%); zenith peaks at y≈28 (12%)
    py: 175 - sinA * 147,
    isMoon,
    nearHorizon,
    t,
  };
}

// ── Static star field ─────────────────────────────────────────────────────────

const STARS = Array.from({ length: 55 }, (_, i) => ({
  cx: parseFloat(((i * 71 + 19) % 100).toFixed(2)),
  cy: parseFloat(((i * 37 + 11) % 68).toFixed(2)),
  r:  [0.9, 1.2, 0.7, 1.4, 0.8][i % 5],
  delay: parseFloat(((i * 0.65) % 5).toFixed(2)),
  dur:   parseFloat((2.4 + (i * 0.28) % 2.2).toFixed(2)),
}));

// ── Sky Scene (SVG) ───────────────────────────────────────────────────────────

function SkyScene({ theme, orb }) {
  const { px, py, isMoon, nearHorizon } = orb;
  const orbR      = nearHorizon ? 22 : (isMoon ? 15 : 14);
  const haloR     = nearHorizon ? 80 : 58;

  return (
    <svg
      className="ciw-sky-svg"
      viewBox="0 0 1000 240"
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ciw-sg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={theme.top} />
          <stop offset="52%"  stopColor={theme.mid} />
          <stop offset="100%" stopColor={theme.btm} />
        </linearGradient>
        <radialGradient id="ciw-hg" cx="50%" cy="100%" r="55%" gradientUnits="objectBoundingBox">
          <stop offset="0%"   stopColor={theme.hglow} />
          <stop offset="100%" stopColor="transparent"  />
        </radialGradient>
        <radialGradient id="ciw-og" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={theme.orb}  stopOpacity="0.85" />
          <stop offset="40%"  stopColor={theme.glow} stopOpacity="0.40" />
          <stop offset="100%" stopColor="transparent" stopOpacity="0"   />
        </radialGradient>
        {/* Ground gradient */}
        <linearGradient id="ciw-gg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="rgba(0,0,0,.22)" />
          <stop offset="100%" stopColor="rgba(0,0,0,.58)" />
        </linearGradient>
        {/* clip to above hills so orb disappears behind them */}
        <clipPath id="ciw-above-hills">
          <path d="M0,0 L1000,0 L1000,168 Q840,152 720,162 Q600,145 500,158 Q380,143 260,160 Q140,152 0,165 Z" />
        </clipPath>
      </defs>

      {/* Sky */}
      <rect width="1000" height="240" fill="url(#ciw-sg)" />

      {/* Stars (night only) */}
      {theme.night && STARS.map((s, i) => (
        <circle
          key={i}
          cx={`${s.cx}%`}
          cy={`${s.cy}%`}
          r={s.r}
          fill="white"
          className="ciw-star"
          style={{ animationDelay: `${s.delay}s`, animationDuration: `${s.dur}s` }}
        />
      ))}

      {/* Horizon atmospheric glow */}
      <rect width="1000" height="240" fill="url(#ciw-hg)" />

      {/* Orb group — clipped so it hides behind hills */}
      <g clipPath="url(#ciw-above-hills)">
        {/* Outer soft halo */}
        <circle cx={px} cy={py} r={haloR}    fill={theme.glow}  className="ciw-orb-halo" />
        {/* Inner glow */}
        <circle cx={px} cy={py} r={orbR + 8} fill={theme.glow}  opacity="0.55" />
        {/* Orb body */}
        <circle cx={px} cy={py} r={orbR}     fill={theme.orb}   className="ciw-orb-body" />
        {/* Moon surface detail */}
        {isMoon && (
          <>
            <circle cx={px - 5} cy={py - 4} r={3.5} fill="rgba(0,0,0,.09)" />
            <circle cx={px + 5} cy={py + 4} r={2.5} fill="rgba(0,0,0,.07)" />
            <circle cx={px + 2} cy={py - 7} r={1.8} fill="rgba(0,0,0,.06)" />
          </>
        )}
        {/* Sun rays when near horizon */}
        {!isMoon && nearHorizon && (
          <ellipse cx={px} cy={180} rx={130} ry={28} fill={theme.hglow} />
        )}
      </g>

      {/* Clouds (day, very subtle) */}
      {theme.clouds && (
        <>
          <g className="ciw-cloud ciw-cloud--a" opacity="0.20">
            <ellipse cx="185" cy="68" rx="72" ry="20" fill="white" />
            <ellipse cx="162" cy="58" rx="44" ry="28" fill="white" />
            <ellipse cx="218" cy="56" rx="40" ry="26" fill="white" />
          </g>
          <g className="ciw-cloud ciw-cloud--b" opacity="0.15">
            <ellipse cx="720" cy="88" rx="58" ry="18" fill="white" />
            <ellipse cx="696" cy="80" rx="36" ry="24" fill="white" />
            <ellipse cx="752" cy="78" rx="32" ry="22" fill="white" />
          </g>
          <g className="ciw-cloud ciw-cloud--c" opacity="0.11">
            <ellipse cx="480" cy="48" rx="42" ry="14" fill="white" />
            <ellipse cx="460" cy="42" rx="28" ry="18" fill="white" />
            <ellipse cx="504" cy="40" rx="26" ry="16" fill="white" />
          </g>
        </>
      )}

      {/* Far hills (lighter) */}
      <path
        d="M0,168 Q80,148 160,162 Q260,143 360,158 Q460,142 560,156 Q660,144 760,160 Q860,150 1000,163 L1000,240 L0,240 Z"
        fill="rgba(0,0,0,.24)"
      />
      {/* Near hills (darker foreground) */}
      <path
        d="M0,190 Q120,178 230,184 Q360,172 480,182 Q600,174 720,183 Q840,176 1000,188 L1000,240 L0,240 Z"
        fill="rgba(0,0,0,.42)"
      />
    </svg>
  );
}

// ── Live clock hook ───────────────────────────────────────────────────────────

function useLiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// ── Main widget ───────────────────────────────────────────────────────────────

export default function CheckInWidget() {
  useTranslation();
  const auth = useSelector((s) => s.auth);
  const now  = useLiveClock();

  const [entry, setEntry]                 = useState(null);
  const [isCheckedIn, setIsCheckedIn]     = useState(false);
  const [isOnBreak, setIsOnBreak]         = useState(false);
  const [liveSeconds, setLiveSeconds]     = useState(0);
  const [breakSeconds, setBreakSeconds]   = useState(0);
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]                 = useState('');
  const [breakWarning, setBreakWarning]   = useState(null);

  const tickRef      = useRef(null);
  const breakTickRef = useRef(null);

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' },
  }), [auth.token]);

  const fetchToday = useCallback(async () => {
    try {
      const res = await axios.get(`/api/attendance/today?workspace_id=${auth.workspace_id}`, authHeaders());
      const d = res.data;
      setEntry(d.entry);
      setIsCheckedIn(d.is_checked_in);
      setIsOnBreak(d.is_on_break);
      setLiveSeconds(d.live_worked_seconds || 0);
      const breakStart = d.active_break?.break_start || null;
      if (breakStart) {
        setBreakSeconds(Math.floor((Date.now() - new Date(breakStart)) / 1000));
      } else {
        setBreakSeconds(0);
      }
    } catch { /* non-critical */ } finally {
      setLoading(false);
    }
  }, [auth.workspace_id, authHeaders]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      fetchToday();
    }, 60000);

    return () => window.clearInterval(interval);
  }, [fetchToday]);

  useEffect(() => {
    const handler = (event) => {
      const payload = event.detail || {};
      if (payload.workspace_id && String(payload.workspace_id) !== String(auth.workspace_id)) return;
      fetchToday();
    };
    window.addEventListener('minutezilla:attendance-changed', handler);
    return () => window.removeEventListener('minutezilla:attendance-changed', handler);
  }, [auth.workspace_id, fetchToday]);

  useEffect(() => {
    clearInterval(tickRef.current);
    if (isCheckedIn && !isOnBreak) {
      tickRef.current = setInterval(() => setLiveSeconds((s) => s + 1), 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [isCheckedIn, isOnBreak]);

  useEffect(() => {
    clearInterval(breakTickRef.current);
    if (isOnBreak) {
      breakTickRef.current = setInterval(() => setBreakSeconds((s) => s + 1), 1000);
    }
    return () => clearInterval(breakTickRef.current);
  }, [isOnBreak]);

  useEffect(() => {
    if (!isCheckedIn || isOnBreak) {
      setBreakWarning(null);
      return;
    }

    const warningKey = entry?.check_in ? new Date(entry.check_in).toISOString() : 'active';
    const sixHourKey = `mz_break_warn_6_${warningKey}`;
    const nineHourKey = `mz_break_warn_9_${warningKey}`;

    if (liveSeconds >= 9 * 3600) {
      setBreakWarning({
        level: 'critical',
        message: tr("You have worked more than 9 hours. Please make sure you have recorded at least 45 minutes of break time."),
      });
      try { sessionStorage.setItem(nineHourKey, '1'); } catch (_) {}
      return;
    }

    if (liveSeconds >= 6 * 3600) {
      setBreakWarning({
        level: 'warning',
        message: tr("You have worked more than 6 hours. Please make sure you record at least a 30-minute break."),
      });
      try { sessionStorage.setItem(sixHourKey, '1'); } catch (_) {}
      return;
    }

    setBreakWarning(null);
  }, [entry?.check_in, isCheckedIn, isOnBreak, liveSeconds]);

  const callApi = async (path, method = 'POST') => {
    setError('');
    setActionLoading(true);
    try {
      await axios({ method, url: `/api/attendance/${path}`, data: JSON.stringify({ workspace_id: auth.workspace_id }), ...authHeaders() });
      await fetchToday();
    } catch (err) {
      const data   = err.response?.data;
      const status = err.response?.status;
      if (status === 409) {
        await fetchToday();
        if (!data?.rest_violation) return;
      }
      let msg = data?.errors?.[0]?.msg || tr("Something went wrong");
      if (data?.rest_violation && data?.hours_remaining) msg = `Rest period: ${data.hours_remaining}h remaining (ArbZG §5)`;
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return null;

  const h            = now.getHours();
  const firstName    = auth.first_name || '';
  const hasCompletedSession = !!entry?.check_out;
  const clockStr     = now.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr      = now.toLocaleDateString(getLocale(), { weekday: 'long', month: 'long', day: 'numeric' });
  const checkInTime  = entry?.check_in  ? formatTime(entry.check_in)  : null;
  const checkOutTime = entry?.check_out ? formatTime(entry.check_out) : null;

  // ── STATE 1: Not checked in — full sky hero ──────────────────────────────────
  // PREVIEW: set to true and change previewHour (0–23) to test different sky states
  const PREVIEW_SKY = false;
  const previewHour = 7; // try 6 (sunrise), 12 (midday), 18 (sunset), 22 (night)
  const previewNow  = PREVIEW_SKY ? new Date(new Date().setHours(previewHour, 30, 0)) : now;

  if (PREVIEW_SKY || !isCheckedIn) {
    const theme = getSkyTheme(PREVIEW_SKY ? previewHour : h);
    const orb   = getOrbPosition(previewNow);

    return (
      <div className="ciw-hero">
        <SkyScene theme={theme} orb={orb} />

        <div className="ciw-hero__overlay">
          <div className="ciw-hero__left">
            <div className="ciw-hero__greeting">
              <span className={`ciw-hero__dot${theme.night ? ' ciw-hero__dot--moon' : ''}`} />
              {getGreeting(h)}{firstName ? `, ${firstName}` : ''}
            </div>
            <div className="ciw-hero__clock">{clockStr}</div>
            <div className="ciw-hero__date">{dateStr}</div>
          </div>

          <div className="ciw-hero__right">
            <p className="ciw-hero__prompt">
              {hasCompletedSession ? tr("Your last session is complete") : tr("You haven't checked in yet")}
            </p>
            {hasCompletedSession && (
              <p className="ciw-hero__date">{tr("Last session:")} {checkInTime} → {checkOutTime}
                {entry?.break_seconds > 0 && <>&nbsp;·&nbsp;{formatDuration(entry.break_seconds)} {tr("breaks")}</>}
              </p>
            )}
            {error && <p className="ciw-hero__error">{error}</p>}
            <button
              className="ciw-hero__btn"
              onClick={() => callApi('check-in')}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <span className="ciw-spinner" />
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                    <polyline points="10 17 15 12 10 7"/>
                    <line x1="15" y1="12" x2="3" y2="12"/>
                  </svg>
                  {hasCompletedSession ? tr("Start Next Session") : tr("Check In Now")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── STATE 2: Checked in — sky persists, content changes ─────────────────────
  if (isCheckedIn) {
    const theme       = getSkyTheme(PREVIEW_SKY ? previewHour : h);
    const orb         = getOrbPosition(previewNow);
    const progressPct = Math.min(100, Math.round((liveSeconds / (8 * 3600)) * 100));

    return (
      <div className={`ciw-hero${isOnBreak ? ' ciw-hero--break' : ''}`}>
        <SkyScene theme={theme} orb={orb} />

        {/* subtle tint when on break */}
        {isOnBreak && <div className="ciw-break-tint" />}

        <div className="ciw-hero__overlay">
          <div className="ciw-hero__left">
            <div className="ciw-hero__greeting">
              <span className="ciw-hero__dot ciw-hero__dot--active" />
              {isOnBreak ? tr("On break") : 'Working'}
              {checkInTime && <span className="ciw-hero__since">{tr("since")} {checkInTime}</span>}
            </div>
            <div className="ciw-hero__clock ciw-hero__clock--timer">{formatElapsed(liveSeconds)}</div>
            {isOnBreak ? (
              <div className="ciw-break-timer">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>{tr("Break:")} {formatElapsed(breakSeconds)}
              </div>
            ) : (
              <div className="ciw-progress-wrap">
                <div className="ciw-progress-bar">
                  <div className="ciw-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
                <span className="ciw-progress-label">{progressPct}{tr("% of 8h")}</span>
              </div>
            )}
          </div>

          <div className="ciw-hero__right">
            {error && <p className="ciw-hero__error">{error}</p>}
            {breakWarning && (
              <div className={`ciw-break-warning ciw-break-warning--${breakWarning.level}`}>
                {breakWarning.message}
              </div>
            )}
            <div className="ciw-hero__actions">
              {!isOnBreak ? (
                <button className="ciw-sky-btn ciw-sky-btn--ghost" onClick={() => callApi('break/start')} disabled={actionLoading}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
                  </svg>
                  {actionLoading ? '…' : tr("Break")}
                </button>
              ) : (
                <button className="ciw-sky-btn ciw-sky-btn--amber" onClick={() => callApi('break/end')} disabled={actionLoading}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  {actionLoading ? '…' : tr("End Break")}
                </button>
              )}
              <button className="ciw-sky-btn ciw-sky-btn--red" onClick={() => callApi('check-out')} disabled={actionLoading}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                {actionLoading ? '…' : tr("Check Out")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
